import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles, RotateCcw, Save, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../api/client';

interface PreferencesResponse {
  summaryPrompt: string | null;
  defaultSummaryPrompt: string;
}

export function SummaryPromptSettings() {
  const queryClient = useQueryClient();
  const [prompt, setPrompt] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [showSaved, setShowSaved] = useState(false);

  // Fetch current preferences
  const { data, isLoading } = useQuery({
    queryKey: ['user-preferences'],
    queryFn: () => api.get('user/preferences').json<PreferencesResponse>(),
  });

  // Update when data loads
  useEffect(() => {
    if (data) {
      setPrompt(data.summaryPrompt || data.defaultSummaryPrompt);
      setIsDirty(false);
    }
  }, [data]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (summaryPrompt: string | null) =>
      api.patch('user/preferences', { json: { summaryPrompt } }).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
      setIsDirty(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
      toast.success('Prompt gespeichert');
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });

  const handleSave = () => {
    // If prompt matches default, save null (use default)
    const valueToSave = prompt === data?.defaultSummaryPrompt ? null : prompt;
    saveMutation.mutate(valueToSave);
  };

  const handleReset = () => {
    if (data?.defaultSummaryPrompt) {
      setPrompt(data.defaultSummaryPrompt);
      setIsDirty(true);
    }
  };

  const isUsingDefault = !data?.summaryPrompt || prompt === data?.defaultSummaryPrompt;

  if (isLoading) {
    return (
      <section className="bg-bg-surface border border-border-default rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-bg-surface border border-border-default rounded-xl p-6">
      <div className="flex items-center gap-3 mb-2">
        <Sparkles className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-medium text-text-primary">KI-Zusammenfassung</h2>
        {isUsingDefault && (
          <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">Standard</span>
        )}
      </div>
      <p className="text-sm text-text-muted mb-4">
        Passe den Prompt für die automatischen E-Mail-Zusammenfassungen an.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-muted mb-2">System-Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value);
              setIsDirty(true);
            }}
            rows={8}
            className="w-full px-3 py-2 bg-bg-primary border border-border-default rounded-lg text-text-primary text-sm font-mono resize-y focus:border-primary focus:outline-none"
            placeholder="Prompt für E-Mail-Zusammenfassungen..."
          />
          <p className="text-xs text-text-muted mt-1">
            Die E-Mail wird als User-Message mit Absender, Betreff und Inhalt übergeben.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={!isDirty || saveMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : showSaved ? (
              <Check className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {showSaved ? 'Gespeichert!' : 'Speichern'}
          </button>
          
          {!isUsingDefault && (
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 text-text-muted hover:text-text-secondary hover:bg-bg-surface-hover rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Auf Standard zurücksetzen
            </button>
          )}
        </div>

        {!isUsingDefault && (
          <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
            <p className="text-xs text-warning">
              Du verwendest einen angepassten Prompt. Bereits generierte Zusammenfassungen werden nicht neu erstellt.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
